<template>

  <v-container fluid class="page page-provider-order-submit">

    <!-- Breadcrumb -->
    <v-row class="mt-4 mb-10">
      <v-col cols="12" xs="12" sm="12" md="8" lg="9" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2">mdi-arrow-top-right-thin-circle-outline</v-icon>
        <router-link :to="{ name: 'provider-order-list' }">Submit Orders</router-link>
        <div class="separator">/</div>
        <div class="active-item">{{ breadcrumbActiveItem }}</div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="4" lg="3" class="d-flex align-center justify-center justify-md-end">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon class="mr-2" color="#727272" style="font-size:20px;cursor:pointer;" v-bind="attrs" v-on="on">
              mdi-information-outline
            </v-icon>
          </template>
          <div v-if="isTestMode">Click to disable test mode</div>
          <div v-else>Enable test mode to submit a test order</div>
        </v-tooltip>
        <v-btn x-large color="orange darken-4" :disabled="isSubmitProcessing" @click="toggleTestMode" v-if="isTestMode">
          Test Mode Enabled
        </v-btn>
        <v-btn x-large outlined color="orange darken-4" :disabled="isSubmitProcessing" @click="toggleTestMode" v-else>
          Submit in Test Mode
        </v-btn>
      </v-col>
    </v-row>

    <v-container fluid style="max-width:1320px;" class="pt-4 page-content" :class="(isTestMode) ? 'test-mode' : ''">

      <!----------------------------------------------------------------------->
      <!--------------------------------------------- Submit Processing Flow -->
      <!----------------------------------------------------------------------->
      <v-container style="max-width:680px;" v-if="isSubmitProcessing">
        <v-row>
          <v-col align="center" style="height:80px;" class="pt-0">
            <div class="mt-4 submitting-text-title" v-if="isSubmitComplete">Order Submission Completed</div>
            <div class="submitting-text-title" v-else-if="hasSubmissionErrors">Submission Did Not Complete</div>
            <div class="submitting-text-title" v-else>Submitting data to {{ appName }}</div>
            <div class="mt-2 submitting-subtext" v-if="hasSubmissionErrors">
              Please resolve the issues to complete the submission.
            </div>
            <div class="mt-2 submitting-subtext" v-else-if="!isSubmitComplete">
              Do not refresh the page until the processing has completed.
            </div>
          </v-col>
        </v-row>

        <!-------------------------------------------- Order Submitting Flow -->
        <!-- (order submitting) -->
        <template v-if="isOrderSubmitting">
          <v-row class="mt-3">
            <v-col class="d-flex align-center justify-start">
              <v-progress-circular indeterminate :size="18" color="primary" />
              <div class="ml-4 loading-text">Submitting order...</div>
            </v-col>
          </v-row>
        </template>

        <!-- (order submit error) -->
        <template v-else-if="orderSubmitError">
          <v-row class="mt-3">
            <v-col class="d-flex align-center justify-start">
              <v-icon color="amber darken-1" class="mr-4">mdi-alert-outline</v-icon>
              <div class="loading-text">There was a problem submitting the order</div>
            </v-col>
          </v-row>
          <v-row class="mt-4 align-center justify-center error-item">
            <v-col class="d-flex align-center justify-center">
              <v-icon color="red" class="mr-2">mdi-alert-circle-outline</v-icon>
              <div>{{ orderSubmitError }}</div>
            </v-col>
          </v-row>
          <v-row class="mt-8">
            <v-col align="center">
              <v-btn text color="primary" @click="loadPage">
                <v-icon class="mr-2" style="font-size:16px" color="primary">mdi-arrow-left</v-icon>
                Return to order
              </v-btn>
            </v-col>
          </v-row>
        </template>

        <!-- (order submitted message) -->
        <template v-else-if="createdOrder">
          <v-row class="mt-3">
            <v-col class="d-flex align-center justify-start">
              <v-icon color="green" class="mr-4">mdi-check-circle</v-icon>
              <template v-if="isTestMode">
                <div class="submitted-text" v-if="credit">Credit issued (Test Mode)</div>
                <div class="submitted-text" v-else>Order submitted (Test Mode)</div>
              </template>
              <template v-else>
                <div class="submitted-text" v-if="credit">Credit issued</div>
                <div class="submitted-text" v-else>Order submitted</div>
              </template>
            </v-col>
          </v-row>
        </template>

        <!----------------------------------------- Documents Uploading Flow -->
        <!-- (supporting docs uploading) -->
        <template v-if="isSupportingDocsUploading">
          <v-row class="mt-4">
            <v-col class="d-flex align-center justify-start submitting-status-row">
              <v-progress-circular indeterminate :size="18" color="primary" />
              <div class="ml-4 loading-text">Uploading supporting docs...</div>
            </v-col>
          </v-row>
        </template>

        <!-- (supporting docs upload errors) -->
        <template v-else-if="supportingDocsUploadErrors && supportingDocsUploadErrors.length > 0">
          <v-row class="mt-4">
            <v-col class="d-flex align-center justify-start submitting-status-row">
              <v-icon color="amber darken-1" class="mr-4">mdi-alert-outline</v-icon>
              <div class="loading-text">Issue uploading supporting docs</div>
              <reupload-order-documents
                  class="ml-5"
                  docType="supporting"
                  :showNote="false"
                  :buttonOutlined="true"
                  buttonColor="amber darken-1"
                  buttonText="Re-upload Supporting Docs"
                  :order="createdOrder"
                  @success="resolveSupportingDocReupload"
                  @failure="resolveSupportingDocReupload" />
            </v-col>
          </v-row>
          <v-row class="mt-0">
            <v-col class="pt-1">
              <v-btn plain icon color="#a9a9a9" style="margin-left:78px" @click="expandSupportingDocErrors = !expandSupportingDocErrors">
                View errors
                <v-icon v-if="!expandSupportingDocErrors">mdi-chevron-down</v-icon>
                <v-icon v-else>mdi-chevron-up</v-icon>
              </v-btn>
              <transition name="fade" appear>
                <div v-if="expandSupportingDocErrors">
                  <div class="mt-2 ml-10" v-for="(error, index) in supportingDocsUploadErrors" :key="`aderr-${index}`">
                    {{ error }}
                  </div>
                </div>
              </transition>
            </v-col>
          </v-row>
        </template>

        <!-- (supporting docs uploaded) -->
        <template v-else-if="supportingDocsUploaded">
          <v-row class="mt-4">
            <v-col class="d-flex align-center justify-start submitting-status-row">
              <v-icon color="green" class="mr-4">mdi-check-circle</v-icon>
              <div class="submitted-text">Uploaded {{ supportingDocsUploaded }} supporting docs</div>
            </v-col>
          </v-row>
        </template>

        <!-- (attachment docs uploading) -->
        <template v-if="isAttachmentDocsUploading">
          <v-row class="mt-4">
            <v-col class="d-flex align-center justify-start submitting-status-row">
              <v-progress-circular indeterminate :size="20" color="primary" />
              <div class="ml-4 loading-text">Uploading attachment docs...</div>
            </v-col>
          </v-row>
        </template>

        <!-- (attachment docs upload errors) -->
        <template v-else-if="attachmentDocsUploadErrors && attachmentDocsUploadErrors.length > 0">
          <v-row class="mt-4">
            <v-col class="d-flex align-center justify-start submitting-status-row">
              <v-icon color="amber darken-1" class="mr-4">mdi-alert-outline</v-icon>
              <div class="loading-text">Issue uploading attachment docs</div>
              <reupload-order-documents
                  class="ml-4"
                  docType="attachment"
                  :showNote="false"
                  :buttonOutlined="true"
                  buttonColor="amber darken-1"
                  buttonText="Re-upload Attachment Docs"
                  :order="createdOrder"
                  @success="resolveAttachmentDocReupload"
                  @failure="resolveAttachmentDocReupload" />
            </v-col>
          </v-row>
          <v-row class="mt-0">
            <v-col class="pt-1">
              <v-btn plain icon color="#a9a9a9" style="margin-left:78px" @click="expandAttachmentDocErrors = !expandAttachmentDocErrors">
                View errors
                <v-icon v-if="!expandAttachmentDocErrors">mdi-chevron-down</v-icon>
                <v-icon v-else>mdi-chevron-up</v-icon>
              </v-btn>
              <transition name="fade" appear>
                <div v-if="expandAttachmentDocErrors">
                  <div class="mt-2 ml-10" v-for="(error, index) in attachmentDocsUploadErrors" :key="`aderr-${index}`">
                    {{ error }}
                  </div>
                </div>
              </transition>
            </v-col>
          </v-row>
        </template>

        <!-- (attachment docs uploaded) -->
        <template v-else-if="attachmentDocsUploaded">
          <v-row class="mt-4">
            <v-col class="d-flex align-center justify-start submitting-status-row">
              <v-icon color="green" class="mr-4">mdi-check-circle</v-icon>
              <div class="submitted-text">Uploaded {{ attachmentDocsUploaded }} attachment docs</div>
            </v-col>
          </v-row>
        </template>

        <!---------------------------------------- Notification Sending Flow -->
        <!-- (notification sending) -->
        <template v-if="isNotificationSending">
          <v-row class="mt-4">
            <v-col class="d-flex align-center justify-start submitting-status-row">
              <v-progress-circular indeterminate :size="18" color="primary" />
              <div class="ml-4 loading-text">Sending notifications...</div>
            </v-col>
          </v-row>
        </template>

        <!-- (notification error) -->
        <template v-else-if="notificationSendingError">
          <v-row class="mt-4">
            <v-col class="d-flex align-center justify-start submitting-status-row">
              <v-icon color="amber darken-1" class="mr-4">mdi-alert-outline</v-icon>
              <div class="loading-text">Issue sending emails</div>
              <v-btn outlined height="34" color="amber darken-1" class="ml-5">
                Re-send Emails
              </v-btn>
            </v-col>
          </v-row>
        </template>

        <!-- (supporting docs uploaded) -->
        <template v-else-if="notificationsSent">
          <v-row class="mt-4">
            <v-col class="d-flex align-center justify-start submitting-status-row">
              <v-icon color="green" class="mr-4">mdi-check-circle</v-icon>
              <div class="submitted-text">Emails sent</div>
            </v-col>
          </v-row>
        </template>

        <!--------------------------------------- Submit Processing Complete -->
        <template v-if="isSubmitComplete">
          <v-row class="mt-6">
            <v-col class="d-flex align-center justify-center">
              <v-btn text color="primary" @click="goToCreditsPage" v-if="credit">
                <v-icon class="mr-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
                Go to Credits
              </v-btn>
              <v-btn text color="primary" @click="goOrdersPage" v-else>
                <v-icon class="mr-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
                Go to Orders
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-container>
      <!-- END - submit processing flow -->

      <!----------------------------------------------------------------------->
      <!-------------------------------------------------- Already Submitted -->
      <!----------------------------------------------------------------------->
      <template v-else-if="isAlreadySubmitted">
        <v-row class="mt-2">
          <v-col class="d-flex align-center justify-center">
            <div class="submitted-text" v-if="credit">Credit has been issued</div>
            <div class="submitted-text" v-else>Order has been submitted</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="d-flex align-center justify-center">
            <v-btn text color="primary" @click="goToCreditsPage" v-if="credit">
              <v-icon class="mr-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
              Go to Credits
            </v-btn>
            <v-btn text color="primary" @click="goOrdersPage" v-else>
              <v-icon class="mr-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
              Go to Orders
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <!----------------------------------------------------------------------->
      <!------------------------------------------------------- Page Loading -->
      <!----------------------------------------------------------------------->
      <v-row v-else-if="isPageLoading">
        <v-col align="center" class="pt-10 pb-8">
          <v-progress-circular indeterminate :size="40" color="primary" />
        </v-col>
      </v-row>

      <!-- (data loading error) -->
      <template v-else-if="dataLoadingError">
        <v-row class="mt-2">
          <v-col class="submitted-text" align="center">
            <div>The was a problem loading the order.</div>
          </v-col>
        </v-row>
        <v-row class="mt-8">
          <v-col align="center">
            <v-row class="mb-5 align-center justify-center error-item">
              <v-col cols="1" align="center">
                <v-icon color="red">mdi-alert-circle-outline</v-icon>
              </v-col>
              <v-col cols="11" align="left">
                <span>{{ dataLoadingError }}</span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="mt-0">
          <v-col align="center">
            <v-btn text color="primary" @click="loadPage">
              <v-icon class="mr-2" style="font-size:16px" color="primary">mdi-refresh</v-icon>
              Retry
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <!----------------------------------------------------------------------->
      <!------------------------------ Provider Order Info & Submission Form -->
      <!----------------------------------------------------------------------->
      <template v-else-if="order">
        <!-- Page Label and Refresh Provider Data -->
        <v-row class="pt-2 mb-3">
          <v-col cols="12" xs="12" sm="7" md="8" lg="9">
            <span style="font-size:1.95em;">{{ formTitle }}</span>
          </v-col>
          <v-col cols="12" xs="12" sm="5" md="4" lg="3" class="d-flex align-center justify-center justify-sm-end">
            <v-btn text color="primary" class="ml-2 mb-2" @click="loadProviderOrderData" v-if="!isProviderDataLoading">
              <v-icon style="font-size:19px;" class="pr-2">mdi-refresh</v-icon>
              <span>Refresh order info</span>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Provider Order Info and Map -->
        <v-row :class="['ma-0', 'pa-0', credit ? 'provider-credit-info' : 'provider-order-info']">
          <v-col cols="12" xs="12" sm="12" md="5" lg="5">
            <div style="font-size:24px">
              Order: {{ order.thirdparty_order_number }}
            </div>
            <div class="mt-4">
              <span class="mr-2 label">Provider:</span>
              <span style="font-size:1.1em;">{{ providerNameForDisplay }}</span>
            </div>
            <div class="mt-2" v-if="order.thirdparty_account_id">
              <span class="mr-2 label">Account:</span>
              <span style="font-size:1.1em;" v-if="activeProviderConnection && activeProviderConnection.account">
                {{ activeProviderConnection.account.name }}
              </span>
            </div>
            <div class="mt-2" v-if="order.thirdparty_brand_id">
              <span class="mr-2 label">Brand:</span>
              <span style="font-size:1.1em;">{{ formatBrandName(order.thirdparty_brand_id) }}</span>
            </div>
            <div class="mt-4" v-if="providerInvoiceUrl">
              <a :href="providerInvoiceUrl" :title="providerInvoiceUrl" target="_new">{{ providerNameForDisplay }} Invoice</a>
              <v-icon class="ml-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
            </div>
            <div class="mt-12" style="text-align:center;" v-if="isProviderDataLoading">
              <v-progress-circular indeterminate :size="40" color="primary" />
            </div>
            <template v-else>
              <div class="mt-11">
                <span class="mr-2 label">Provider Status:</span>
                <v-chip>{{ order.thirdparty_status }}</v-chip>
              </div>
              <template v-if="orderDetails">
                <div class="mt-8 text-h6" v-if="orderedItemCount">
                  <span class="label">Items:</span>
                  {{ orderedItemCount }}
                </div>
                <div class="mt-3 text-h6">
                  <span class="label">{{ amountLabelForDisplay }}:</span>
                  ${{ amountForDisplay }}
                </div>
              </template>
            </template>
          </v-col>

          <!-- Map Viewer -->
          <v-col cols="12" xs="12" sm="12" md="7" lg="7" align="center">
            <map-viewer
                v-if="mapSettings"
                :settings="mapSettings"
                :markers=distributions />
            <template v-else>
              <v-progress-circular indeterminate :size="30" class="mt-8" color="primary" />
            </template>
          </v-col>
        </v-row>

        <!-- Shipper / Receiver Info -->
        <v-row class="mt-8 mb-0">
          <!-- (From) -->
          <v-col cols="12" xs="12" sm="6" md="6" lg="6" align="center" class="pb-8">
            <div class="text-h6 mb-3">
              <v-icon style="font-size:27px;padding-bottom:2px;" color="white" class="mr-2" v-if="credit">mdi-credit-card</v-icon>
              <v-icon large color="white" class="mr-1" v-else>mdi-map-marker-outline</v-icon>
              <span>{{ shippingFromTitle }}</span>
            </div>
            <div v-if="pickupLoc">
              <div class="location-name">{{ shipperLocationName }}</div>
              <div class="mt-2 location-address">
                <div>{{ shipperMultilineAddress.street_1 }}</div>
                <span v-if="shipperMultilineAddress.street_2">,</span> {{ shipperMultilineAddress.street_2 }}
                <template v-if="shipperMultilineAddress.area">{{ shipperMultilineAddress.area }}</template>
              </div>
            </div>
          </v-col>
          <!-- (To) -->
          <v-col cols="12" xs="12" sm="6" md="6" lg="6" align="center">
            <div class="text-h6 mb-3">
              <v-icon style="font-size:27px;padding-bottom:2px;" color="white" class="mr-2" v-if="credit">mdi-credit-card</v-icon>
              <v-icon large color="white" class="mr-1" v-else>mdi-map-marker-outline</v-icon>
              <span>{{ shippingToTitle }}</span>
            </div>
            <div v-if="dropoffLoc">
              <div class="location-name">{{ receiverLocationName }}</div>
              <div class="mt-2 location-address">
                <div>{{ receiverMultilineAddress.street_1 }}</div>
                <span v-if="shipperMultilineAddress.street_2">,</span> {{ receiverMultilineAddress.street_2 }}
                <template v-if="receiverMultilineAddress.area">{{ receiverMultilineAddress.area }}</template>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Receiver Resolution -->
        <v-row class="mt-0 mb-5" v-if="receiversToResolve && receiversToResolve.length > 0">
          <v-col cols="12" align="center">
            <div class="mb-3 d-flex align-center justify-center">
              <v-icon style="color:#f8a824;font-size:28px;" class="mr-3">mdi-alert-circle-outline</v-icon>
              <span style="color:#f8a824;font-size:1.1rem">Please confirm the intended receiver location:</span>
            </div>
            <provider-order-receiver-selector
                :selections="receiversToResolve"
                v-model="resolvedReceiverLocation" />
          </v-col>
        </v-row>

        <!-- Ordered Items -->
        <v-row v-if="isProviderDataLoading">
          <v-col cols="12" align="center">
            <v-progress-circular indeterminate :size="24" color="white" />
          </v-col>
        </v-row>
        <v-row class="mt-0 px-4" v-else>
          <v-col class="d-flex flex-column align-center justify-center">
            <v-btn plain icon @click="toggleOrderedItems">
              {{ orderedItemsTitle }}
              <v-icon v-if="!expandOrderedItems">mdi-chevron-down</v-icon>
              <v-icon v-else>mdi-chevron-up</v-icon>
            </v-btn>
            <transition name="fade" appear>
              <order-shipment-details
                  v-if="expandOrderedItems"
                  :showHeading="false"
                  :showNotes="false"
                  :order="order" />
            </transition>
          </v-col>
        </v-row>

        <v-divider class="my-4 mt-10" />

        <!-- Supporting Docs and Attachment Docs -->
        <order-document-uploader ref="doc-uploader" class="mt-6" />

        <v-divider class="my-4" />

        <!-- Expected Date selection (for standard orders only) -->
        <template v-if="!credit">
          <!-- Appointment handling -->
          <v-row class="mt-3" v-if="receiverRequiresAppointment">
            <v-col class="d-flex align-center justify-center">
              <div class="px-8 py-4 d-flex flex-column align-center justify-center requires-appt-container">
                <div class="requires-appt-title">
                  {{ resolvedReceiverLocation.name }} requires an appointment for delivery
                </div>
                <div class="mt-3 requires-appt-info-text">
                  You can make the appointment now or when scheduling the order in a route.
                </div>
                <div class="mt-3 d-flex align-center justify-center" v-if="resolvedReceiverLocation.appointment_url">
                  <a class="item-link" :href="formatUrl(resolvedReceiverLocation.appointment_url)" :title="resolvedReceiverLocation.appointment_url" target="_new">Appointment website</a>
                  <v-icon class="ml-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
                  <v-tooltip bottom v-if="resolvedReceiverLocation.notes">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon class="ml-4" color="grey lighten-1" style="font-size:20px;cursor:pointer;" v-bind="attrs" v-on="on">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <div>{{ resolvedReceiverLocation.notes }}</div>
                  </v-tooltip>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row class="mt-3">
            <v-col class="d-flex flex-column align-center justify-center">
              <v-switch
                  inset
                  label="Specify expected delivery date?"
                  v-model="includeDeliveryDate"
                  @change="clearDeliveryDate" />
              <div class="mt-3" style="color:#9a9a9a;font-size:1.08rem;" v-if="!includeDeliveryDate">
                No delivery date provided.
              </div>
            </v-col>
          </v-row>
          <template v-if="includeDeliveryDate">
            <transition name="fade-fast" appear>
              <v-container>
                <v-row>
                  <v-col align="center">
                    <div class="text-h5">Expected Delivery Date:</div>
                  </v-col>
                </v-row>
                <v-row class="ma-0 pa-0">
                  <v-col align="center" class="ma-0 pa-0">
                    <v-dialog persistent max-width="350" v-model="isDeliveryDateSelectorOpen" align="center">
                      <!-- Select Date Dialog (activator links) -->
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn text class="mt-7" color="primary" v-bind="attrs" v-on="on" v-if="!order.expected_delivery_date">
                          <v-icon style="font-size:28px;" class="mr-3" color="primary">mdi-calendar</v-icon>
                          <span style="font-size:1.0rem;">Select a delivery date</span>
                        </v-btn>
                        <v-btn text class="mt-7" color="white" v-bind="attrs" v-on="on" v-else>
                          <span style="font-size:1.1rem;">{{ formatDeliveryDate(order.expected_delivery_date) }}</span>
                          <v-icon class="ml-4" color="primary">mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <!-- (date picker) -->
                      <v-card>
                        <v-card-text class="date-picker-body">
                          <v-date-picker v-model="selectedDeliveryDate" />
                        </v-card-text>
                        <v-card-actions>
                          <v-btn text @click="isDeliveryDateSelectorOpen = false">
                            Cancel
                          </v-btn>
                          <v-spacer />
                          <v-btn text color="grey" @click="clearDeliveryDate">
                            <v-icon left dark>mdi-close</v-icon>
                            Clear Date
                          </v-btn>
                          <v-spacer />
                          <v-btn text color="primary" @click="setDeliveryDate">
                            Select
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </v-col>
                </v-row>
              </v-container>
            </transition>
          </template>
        </template>

        <!-- Submit Button -->
        <v-row class="mt-7 mb-4">
          <v-col style="text-align:center" class="submit-button-area">
            <dialog-confirm-button
                v-if="credit"
                :buttonColor="(isTestMode) ? 'orange darken-4' : 'primary'"
                :buttonHeight="55"
                :buttonWidth="280"
                :buttonText="(isTestMode) ? 'Issue Credit as Test' : 'Issue Credit'"
                :disabled="!isReadyToSubmit || isSubmitProcessing"
                :message="`Issue credit of $${amountForDisplay} to ${receiverLocationName}?`"
                v-on:confirmed="submitOrder" />
            <dialog-confirm-button
                v-else
                :buttonColor="(isTestMode) ? 'orange darken-4' : 'primary'"
                :buttonHeight="55"
                :buttonWidth="280"
                :buttonText="(isTestMode) ? 'Submit as Test' : 'Submit'"
                :disabled="!isReadyToSubmit || isSubmitProcessing"
                :message="confirmOrderSubmitMessage"
                v-on:confirmed="submitOrder" />
          </v-col>
        </v-row>

      </template> <!-- END submission form -->

    </v-container> <!-- END page-content -->

  </v-container>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
/* global moment */
import mapboxUtils from '@/utils/mapbox-utils'
import MapViewer from '@/components/map/MapViewer'
import ProviderOrderReceiverSelector from '@/components/providerOrders/ProviderOrderReceiverSelector'
import OrderDocumentUploader from '@/components/orders/OrderDocumentUploader'
import OrderShipmentDetails from '@/components/orders/OrderShipmentDetails'
import DialogConfirmButton from '@/components/app/DialogConfirmButton'
import ReuploadOrderDocuments from '@/components/orders/ReuploadOrderDocuments'

export default {
  name: 'ProviderOrderSubmitPage',

  components: {
    MapViewer,
    ProviderOrderReceiverSelector,
    OrderDocumentUploader,
    OrderShipmentDetails,
    DialogConfirmButton,
    ReuploadOrderDocuments
  },

  props: {
    orderId: {
      required: true
    },
    providerAlias: {
      type: String,
      required: true
    },
    accountId: {
      type: String,
      default: null
    },
    brandId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      // ----------- Test mode
      isTestMode: false,
      // ----------- Page load handling
      isPageLoading: false,
      isProviderDataLoading: false,
      dataLoadingError: null,
      isAlreadySubmitted: false,
      submissionRecord: null,
      // ----------- Order submission handling
      isSubmitProcessing: false,
      isSubmitComplete: false,
      isOrderSubmitting: false,
      orderSubmitError: null,
      isSupportingDocsUploading: false,
      supportingDocsUploadErrors: [],
      supportingDocsUploaded: 0,
      expandSupportingDocErrors: false,
      isAttachmentDocsUploading: false,
      attachmentDocsUploadErrors: [],
      attachmentDocsUploaded: 0,
      expandAttachmentDocErrors: false,
      isNotificationSending: false,
      notificationSendingError: null,
      notificationsSent: false,
      // ----------- Receiver location handling
      receiversToResolve: [],
      resolvedReceiverLocation: null,
      // ----------- Form options
      expandOrderedItems: false,
      isDeliveryDateSelectorOpen: false,
      selectedDeliveryDate: null,
      includeDeliveryDate: true,
      // ----------- Order info
      credit: this.$route.name === 'provider-order-issue-credit',
      order: null,
      orderDetails: null,
      orderedItems: [],
      createdOrder: null, // RouteLeaf order
      pickupLoc: null,
      dropoffLoc: null,
      // ----------- Map info
      mapSettings: null,
      distributions: [],
      bounds: []
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    breadcrumbActiveItem () {
      const orderLabel = (this.credit) ? 'Credit' : 'Order'
      return (this.order)
        ? `${this.providerNameForDisplay} ${orderLabel} ${this.order.thirdparty_order_number}`
        : ''
    },
    formTitle () {
      return (this.credit) ? 'Issue Credit Memo' : 'Submit Order to RouteLeaf'
    },
    shippingFromTitle () {
      return (this.credit) ? 'Issue From' : 'Shipping From'
    },
    shippingToTitle () {
      return (this.credit) ? 'Credit To' : 'Shipping To'
    },
    orderedItemsTitle () {
      return (this.credit) ? 'Credited Items' : 'Ordered Items'
    },
    amountLabelForDisplay () {
      return (this.credit) ? 'Credit' : 'Total'
    },
    amountForDisplay () {
      if (this.credit) {
        return Number(this.orderDetails.total * -1).toLocaleString(undefined, { minimumFractionDigits: 2 })
      } else {
        return Number(this.orderDetails.total).toLocaleString(undefined, { minimumFractionDigits: 2 })
      }
    },
    providerNameForDisplay () {
      return (this.providerAlias) ? this.$root.providerAliasToName(this.providerAlias) : ''
    },
    activeProviderConnection () {
      return this.$store.getters['sh/providerConnection'](this.providerAlias, this.accountId)
    },
    providerInvoiceUrl () {
      return (this.activeProviderConnection)
        ? this.$root.providerInvoiceUrl(this.order, this.activeProviderConnection)
        : null
    },
    activeLoc () {
      return (this.activeProviderConnection) ? this.activeProviderConnection.shipper : null
    },
    hasReceiversToResolve () {
      return this.receiversToResolve && this.receiversToResolve.length > 0
    },
    isReadyToSubmit () {
      if (!this.credit && this.includeDeliveryDate) {
        return (this.hasReceiversToResolve)
          ? this.resolvedReceiverLocation && this.order.expected_delivery_date
          : this.order.expected_delivery_date
      } else {
        return (this.hasReceiversToResolve)
          ? this.resolvedReceiverLocation
          : true
      }
    },
    confirmOrderSubmitMessage () {
      return (this.order.expected_delivery_date)
        ? `Submit Order ${this.order.thirdparty_order_number} to ${this.appName} for ${this.formatDeliveryDate(this.order.expected_delivery_date, true)}?`
        : `Submit Order ${this.order.thirdparty_order_number} to ${this.appName}?`
    },
    hasSubmissionErrors () {
      return (
        this.orderSubmitError ||
        (this.supportingDocsUploadErrors && this.supportingDocsUploadErrors.length > 0) ||
        (this.attachmentDocsUploadErrors && this.attachmentDocsUploadErrors.length > 0)
      )
    },
    // -------------------- Shipper info
    shipperLocationName () {
      return this.pickupLoc.name
    },
    shipperMultilineAddress () {
      return this.$root.utils.getAddressTextFromData(this.pickupLoc, { multi_lines: true })
    },
    // -------------------- Receiver info
    receiverLocationName () {
      return this.dropoffLoc.name
    },
    receiverMultilineAddress () {
      return this.$root.utils.getAddressTextFromData(this.dropoffLoc, { multi_lines: true })
    },
    receiverRequiresAppointment () {
      return (this.resolvedReceiverLocation) ? this.resolvedReceiverLocation.requires_appointment : false
    },
    // -------------------- Ordered items
    orderedItemCount () {
      return (this.orderDetails) ? this.$root.utils.get(this.orderDetails, 'item_data.item_count', null) : null
    }
  },

  async beforeMount () {
    this.loadPage()
  },

  beforeDestroy () {
    this.reset()
  },

  methods: {
    formatAddress (data = {}) {
      return this.$root.utils.getAddressTextFromData(data)
    },

    formatDeliveryDate (date, noTz) {
      return (noTz === true)
        ? this.$root.utils.format(date, 'dddd, MMMM D, YYYY')
        : this.$root.utils.format(date, 'dddd, MMMM D, YYYY (z)')
    },

    // Returns the brand display name for the provided brand ID
    formatBrandName (brandId) {
      let name = brandId || ''
      if (brandId && this.activeProviderConnection && this.activeProviderConnection.brands) {
        const brand = this.activeProviderConnection.brands.find(item => item.thirdparty_id == brandId) // eslint-disable-line eqeqeq
        if (brand && brand.name) name = brand.name
      }
      return name
    },

    // Ensures url has protocol in front
    formatUrl (url) {
      return (url.startsWith('http://') || url.startsWith('https://'))
        ? url
        : `http://${url}`
    },

    reset () {
      this.dataLoadingError = null
      this.orderSubmitError = null
      this.supportingDocsUploadErrors = []
      this.supportingDocsUploaded = 0
      this.attachmentDocsUploadErrors = []
      this.attachmentDocsUploaded = 0
      this.notificationSendingError = null
      this.submissionRecord = null
      this.receiversToResolve = []
      this.resolvedReceiverLocation = null
      this.bounds = []
      this.distributions = []
      this.pickupLoc = null
      this.dropoffLoc = null
      this.createdOrder = null
      this.selectedDeliveryDate = null
      this.includeDeliveryDate = true
    },

    // Toggle test mode
    toggleTestMode () {
      this.isTestMode = !this.isTestMode
    },

    setDeliveryDate () {
      const selectedDate = this.selectedDeliveryDate
      if (selectedDate) {
        const shiftedDate = moment(selectedDate).add(12, 'hours')
        this.order.expected_delivery_date = this.$root.utils.format(shiftedDate)
      }
      this.isDeliveryDateSelectorOpen = false
    },

    clearDeliveryDate () {
      this.order.expected_delivery_date = null
      this.isDeliveryDateSelectorOpen = false
    },

    toggleOrderedItems () {
      this.expandOrderedItems = !this.expandOrderedItems
    },

    goOrdersPage () {
      this.$root.goTo('order-list-placed')
    },

    goToCreditsPage () {
      this.$root.goTo('credit-list-issued')
    },

    // Load submit page
    async loadPage () {
      this.bounds = []
      this.isSubmitProcessing = false
      this.isOrderSubmitting = false
      this.isSubmitComplete = false
      this.dataLoadingError = null
      this.orderSubmitError = null
      this.notificationSendingError = null
      this.isPageLoading = true

      try {
        // Ensure provider connections are loaded
        await this.loadProviderConnections()

        // Load provider order item with details
        await this.loadProviderOrderData()

        // Generate map locations
        await this.generateMapLocations()
      } catch (err) {
        switch (err.status) {
          case 503: this.dataLoadingError = 'The thirdparty provider is not responding'; break
          default: this.dataLoadingError = err.message || err.text || err
        }
      } finally {
        this.isPageLoading = false
      }
    },

    // Load provider order data
    async loadProviderOrderData () {
      this.isProviderDataLoading = true
      this.dataLoadingError = null
      this.orderSubmitError = null

      // Ensure order not already submitted
      await this.checkIfOrderSubmitted()

      if (!this.isAlreadySubmitted) {
        try {
          // Match receiver with RouteLeaf (for possible resolution scenario)
          const results = await this.matchReceiverWithRouteLeaf()
          if (!results.direct_match && results.matches.length > 0) {
            this.receiversToResolve = results.matches
          } else if (results.matches.length > 0) {
            this.resolvedReceiverLocation = results.matches[0]
          }

          // Load order item with details
          this.order = await this.$store.dispatch('sh/dataApi', {
            method: 'get',
            uri: `provider/order/${this.orderId}`,
            opts: {
              params: {
                provider: this.providerAlias,
                account_id: this.accountId,
                brand_id: this.brandId
              }
            }
          })
          this.orderDetails = this.order.order_details
          this.orderedItems = (this.orderDetails) ? this.orderDetails.item_data.items : []
        } catch (err) {
          switch (err.status) {
            case 503: this.dataLoadingError = 'The thirdparty provider is not responding'; break
            default: this.dataLoadingError = err.message || err.text || err
          }
        }
      }

      this.isProviderDataLoading = false
    },

    // Checks if provider order is already submitted
    async checkIfOrderSubmitted () {
      try {
        this.submissionRecord = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `provider/order/${this.orderId}/status`,
          opts: {
            bodyData: {
              provider: this.providerAlias,
              account_id: this.accountId
            }
          }
        })
        if (this.submissionRecord.status === 'SUBMITTED') this.isAlreadySubmitted = true
      } catch (err) {}
    },

    // Performs matching attempt with existing RouteLeaf receivers (in case shipper must resolve target)
    async matchReceiverWithRouteLeaf () {
      return this.$store.dispatch('sh/dataApi', {
        method: 'post',
        uri: `provider/order/${this.orderId}/match-receiver`,
        opts: {
          bodyData: {
            provider: this.providerAlias,
            account_id: this.accountId,
            brand_id: this.brandId
          }
        },
        to_model: false
      })
    },

    // Load all provider connections
    async loadProviderConnections (force) {
      await this.$store.dispatch('sh/dataApi', {
        method: 'load',
        uri: 'provider-connections',
        opts: {
          resource: 'providerConnections',
          params: { with: 'shipper' },
          all: true,
          force
        }
      })
    },

    async generateMapLocations () {
      if (!this.order) return false

      const shipperInfo = this.order.shipper_info || {}
      const receiverInfo = this.order.receiver_info || {}

      // Determine locations
      this.pickupLoc = (!shipperInfo.location || this.$root.utils.isEmpty(shipperInfo.location))
        ? this.activeLoc // if no shipper location, presume active shipper location for account
        : shipperInfo.location
      this.dropoffLoc = receiverInfo.location

      if (!this.pickupLoc || !this.dropoffLoc) return false // TODO - Handle error scenario !!!

      try {
        // Extract receiver coords for map
        const receiverCoordsData = await mapboxUtils.extractCoordsFromAddressData(this.dropoffLoc)

        // Build distribution pair
        if (receiverCoordsData) {
          const receiverCoords = [
            this.$root.utils.truncateDecimals(receiverCoordsData.longitude, 5),
            this.$root.utils.truncateDecimals(receiverCoordsData.latitude, 5)
          ]

          // Add to bounds
          this.bounds.push(receiverCoords)

          // Build map marker
          this.distributions.push({
            coordinates: receiverCoords,
            properties: {
              name: this.dropoffLoc.name,
              alias: this.dropoffLoc.alias,
              type: 'dropoff-location'
            }
          })

          // Build map settings for display
          this.mapSettings = {
            minZoom: 6,
            maxZoom: 18,
            zoom: 16,
            center: receiverCoords,
            fitBoundsOptions: { padding: { top: 40, bottom: 40, left: 40, right: 40 } }
          }
        }
      } catch (err) {
        console.log(err)
      }
    },

    resolveSupportingDocReupload (results) {
      this.expandSupportingDocErrors = false
      this.supportingDocsUploaded = results.uploaded
      this.supportingDocsUploadErrors = []
      if (results.errors.length > 0) {
        this.supportingDocsUploadErrors = results.errors
      } else if (this.attachmentDocsUploadErrors.length === 0) {
        this.completeOrderSubmission()
      }
    },

    resolveAttachmentDocReupload (results) {
      this.expandAttachmentDocErrors = false
      this.attachmentDocsUploaded = results.uploaded
      this.attachmentDocsUploadErrors = []
      if (results.errors.length > 0) {
        this.attachmentDocsUploadErrors = results.errors
      } else if (this.supportingDocsUploadErrors.length === 0) {
        this.completeOrderSubmission()
      }
    },

    // Submit order/credit
    async submitOrder () {
      this.isSubmitProcessing = true
      this.isOrderSubmitting = true

      // If credit, set delivery date to now
      if (this.credit) {
        this.order.expected_delivery_date = this.$root.utils.format(new Date())
      }

      // Set confirmed receiver
      if (this.resolvedReceiverLocation && this.resolvedReceiverLocation !== 'na') {
        this.order.receiver_id = this.resolvedReceiverLocation.id
      }

      // Access document uploader
      const docUploader = this.$refs['doc-uploader'] || {}
      const hasSupportingDocs = docUploader.hasSupportingDocs
      const hasAttachmentDocs = docUploader.hasAttachmentDocs

      // If supporting docs, build doc info on order
      if (hasSupportingDocs) {
        const docInfo = {}
        for (const doc of docUploader.addedSupportingDocs) {
          if (doc.asset_key && doc.file) {
            docInfo[doc.asset_key] = {
              asset_key: doc.asset_key,
              order: doc.order,
              label: doc.label,
              category: doc.category,
              file_name: doc.file_name,
              file_type: doc.file_type,
              file_extension: doc.file_extension
            }
          }
        }
        this.order.supporting_docs = docInfo
      }

      // If attachment docs, build doc info on order
      if (hasAttachmentDocs) {
        const docInfo = {}
        for (const doc of docUploader.addedAttachmentDocs) {
          if (doc.asset_key && doc.file) {
            docInfo[doc.asset_key] = {
              asset_key: doc.asset_key,
              order: doc.order,
              label: doc.label,
              category: doc.category,
              file_name: doc.file_name,
              file_type: doc.file_type,
              file_extension: doc.file_extension
            }
          }
        }
        this.order.attachment_docs = docInfo
      }

      // Submit order/credit to RouteLeaf
      try {
        const uri = (this.credit)
          ? `/provider/order/${this.order.thirdparty_order_id}/issue-credit`
          : `/provider/order/${this.order.thirdparty_order_id}/submit`
        this.createdOrder = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri,
          opts: {
            bodyData: {
              routeleaf_receiver_id: this.order.receiver_id,
              provider: this.order.provider_alias,
              account_id: this.order.thirdparty_account_id,
              brand_id: this.order.thirdparty_brand_id,
              delivery_date: this.order.expected_delivery_date,
              supporting_docs: this.order.supporting_docs,
              attachment_docs: this.order.attachment_docs,
              is_test: this.isTestMode === true
            }
          }
        })

        // DEVING - success/failure examples
        // this.createdOrder = await new Promise(resolve => {
        //   setTimeout(() => {
        //     resolve({ id: '123', order_number: '#123' })
        //   }, 6000)
        // })
        // this.createdOrder = await new Promise((resolve, reject) => {
        //   setTimeout(() => {
        //     reject(new Error('Failed to create order: Network timeout'))
        //   }, 6000)
        // })
      } catch (err) {
        this.orderSubmitError = err.message || err.text || err
      } finally {
        this.isOrderSubmitting = false
      }

      // On successful submit, upload any documents and send notification
      let hasUploadErrors = false
      if (this.createdOrder) {
        if (hasSupportingDocs) {
          this.isSupportingDocsUploading = true
          const results = await docUploader.uploadSupportingDocs(this.createdOrder.id)

          // DEVING - success/failure examples
          // const results = await new Promise(resolve => {
          //   setTimeout(() => {
          //     resolve({ uploaded: 1, errors: ['Issue uploading Doc A', 'Issue uploading Doc B'] })
          //   }, 4000)
          // })

          if (results.errors && results.errors.length > 0) {
            this.supportingDocsUploadErrors = results.errors
            hasUploadErrors = true
          } else {
            this.supportingDocsUploaded = results.uploaded
          }
          this.isSupportingDocsUploading = false
        }

        if (hasAttachmentDocs) {
          this.isAttachmentDocsUploading = true
          const results = await docUploader.uploadAttachmentDocs(this.createdOrder.id)

          // DEVING - success/failure examples
          // const results = await new Promise(resolve => {
          //   setTimeout(() => {
          //     resolve({ uploaded: 2, errors: ['Issue with Attachment X', 'Issue with Attachment Y'] })
          //   }, 4000)
          // })

          if (results.errors && results.errors.length > 0) {
            this.attachmentDocsUploadErrors = results.errors
            hasUploadErrors = true
          } else {
            this.attachmentDocsUploaded = results.uploaded
          }
          this.isAttachmentDocsUploading = false
        }

        // If order submission and uploads are successful - complete flow and set "data ready"
        if (!hasUploadErrors) this.completeOrderSubmission()
      }
    },

    // Completes the order submission process and sets order to "data ready"
    // (NOTE: this will trigger notifications)
    async completeOrderSubmission () {
      try {
        this.isNotificationSending = true

        const uri = (this.credit)
          ? `credit/${this.createdOrder.id}/data-ready`
          : `order/${this.createdOrder.id}/data-ready`
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri
        })

        // DEVING - success/failure examples
        // await new Promise(resolve => {
        //   setTimeout(() => {
        //     resolve({ success: true })
        //   }, 4000)
        // })

        this.notificationsSent = !this.isTestMode
      } catch (err) {
        this.notificationSendingError = err.message || err.text || err
      } finally {
        this.isNotificationSending = false
      }

      this.isSubmitComplete = true
    }
  }
}
</script>

<style>
  .page-provider-order-submit .page-content.test-mode {
    border: 2px solid #E65000;
  }

  .page-provider-order-submit .form-section {
    min-width: 325px;
  }

  .page-provider-order-submit .label {
    color: #9a9a9a;
    font-size: 16px
  }

  .page-provider-order-submit .description-text {
    color: #6a6a6a;
    font-style: italic;
  }

  .page-provider-order-submit .map-viewer {
    height: 380px !important;
  }

  .page-provider-order-submit .provider-order-info {
    border: 1px solid #20add8;
    border-radius: 4px;
  }

  .page-provider-order-submit .provider-credit-info {
    border: 1px solid #20add8;
    border-radius: 4px;
  }

  .page-provider-order-submit .thirdparty-order-id {
    color: #9a9a9a;
    font-size: 15px
  }

  .page-provider-order-submit .location-name {
    font-size: 1.1em;
  }

  .page-provider-order-submit .location-address {
    color: #9a9a9a;
  }

  .page-provider-order-submit .distance-text {
    font-size: 1.14em;
  }

  .page-provider-order-submit .requires-appt-container {
    border: 1px solid #f94851;
    border-radius: 3px;
    /* color: #ffffff; */
  }

  .page-provider-order-submit .requires-appt-title {
    font-size: 1.24rem;
  }

  .page-provider-order-submit .requires-appt-info-text {
    font-size: 1.0rem;
    color: #9c9c9c;
  }

  .page-provider-order-submit .submit-button-area .v-btn__content {
    font-size: 17px !important;
  }

  /* Info text for processing and results display */
  .page-provider-order-submit .loading-text {
    font-size: 1.14rem;
    color: #a2a2a2;
  }

  .page-provider-order-submit .submitting-text-title {
    text-transform: uppercase !important;
    letter-spacing: 1.5px;
    font-size: 0.9rem;
    color: #a2a2a2;
  }

  .page-provider-order-submit .submitting-subtext {
    font-size: 1.12rem;
    color: #d2d2d2;
  }

  .page-provider-order-submit .submitting-status-row {
    height: 60px;
  }

  .page-provider-order-submit .submitted-text {
    font-size: 1.14rem;
  }

  /* Error display */
  .page-provider-order-submit .error-item {
    color: #F44336;
    font-size: 1.0rem;
    border: 1px solid #F44336;
    border-radius: 4px;
    padding: 3px 3px;
    max-width: 500px;
  }

  /* Warning display */
  .page-provider-order-submit .warning-item {
    color: #FFB300;
    font-size: 0.98rem;
    border: 1px solid #FFB300;
    border-radius: 4px;
    padding: 3px 3px;
    max-width: 500px;
  }
</style>
